<template>
    <v-card :title="pgettext('label', 'TIC Card')">
        <v-card-text>
            <Form :ref="(el) => formRef = el"
                  @submit="formSubmit"
                  class="form form__control hpo-patient-modal-form"
                  :validation-schema="schema"
                  validate-on="submit">

                <div class="hpo-patient-modal-form__alert-container">
                    <v-alert v-if="alertMessage" closable type="success" :text="alertMessage"/>
                </div>

                <div class="d-flex ga-6 ga-md-14">
                    <div class="hpo-patient-modal-form__main">
                        <NewFormGroup inputId="ticPrefix" name="ticPrefix" :label="pgettext('label', 'TIC Card Letter')" required>
                            <div style="display: flex">
                                <SelectButton v-for="value of prefixOptions"
                                              :value="value"
                                              v-model="patient.ticPrefix"
                                              :cssClasses="['prefix-option-select-button']">
                                    {{ value }}
                                </SelectButton>
                                <div class="select-button prefix-option-select-button"
                                     @click="showPrefixModal = true">
                                    <div class="prefix-option-select-button__content">
                                        <div>{{ pgettext('action', 'Add new') }}</div>
                                        <i class="fa-sharp fa-solid fa-plus"></i>
                                    </div>
                                </div>
                            </div>
                            <Field type="hidden" name="ticPrefix" id="ticPrefix" v-model="patient.ticPrefix"/>
                        </NewFormGroup>

                        <div class="form__group--new form__group--validation-message-padding mt-2">
                            <label class="form__label" :for="inputId">{{ pgettext('label', 'TIC Card Number') }}<em class="form__required-mark">*</em></label>
                            <div class="hpo-patient-modal-form__tic-wrapper">
                                <Field type="text"
                                       inputmode="numeric"
                                       pattern="[0-9]"
                                       name="ticNumber" id="ticNumber" v-model="patient.ticNumber"
                                       class="hpo-patient-modal-form__tic-input"
                                       maxlength="4"
                                       validate-on-input
                                       autofocus/>
                                <div v-if="useNextTicMessage" class="hpo-patient-modal-form__next-tic-message">
                                    <v-chip @click="useNextTic()">
                                        <span v-html="useNextTicMessage"/>
                                    </v-chip>
                                </div>
                            </div>
                            <ErrorMessage :name="name" :as="p" class="form__error"/>
                        </div>

                        <NewFormGroup inputId="__all__" name="__all__" label="">
                            <Field v-show="false" type="text" name="__all__" id="__all__"/>
                        </NewFormGroup>

                        <NewFormGroup inputId="gender" name="gender" label="">
                            <Field name="gender" id="gender" v-model="patient.gender" v-slot="{ componentField }">
                                <HpoPatientModalGenderSection v-bind="componentField"
                                                              :title="pgettext('label', 'Gender')"/>
                            </Field>
                        </NewFormGroup>

                        <NewFormGroup inputId="isChild" name="isChild" label="">
                            <Field name="isChild" id="isChild" v-model="patient.isChild" v-slot="{ componentField }">
                                <HpoPatientModalIsChildSection v-bind="componentField"
                                                               :title="pgettext('label', 'Adult / child')"/>
                            </Field>
                        </NewFormGroup>

                        <NewFormGroup inputId="priority" name="priority" label="">
                            <Field name="priority" id="priority" v-model="patient.priority" v-slot="{ componentField }">
                                <HpoPatientModalPhcSection v-bind="componentField"/>
                            </Field>
                        </NewFormGroup>
                    </div>
                    <div class="hpo-patient-modal-form__keypad d-none d-sm-block">
                        <TicKeypad v-model="patient.ticNumber" :label="pgettext('label', 'TIC Card Number')"/>
                    </div>
                </div>

                <div class="mt-5">
                    <v-row v-if="createMode">
                        <v-col>
                            <v-btn type="submit"
                                   size="large"
                                   color="indigo"
                                   :loading="loading"
                                   :text="pgettext('action', 'Save and close')"
                                   block/>
                        </v-col>
                        <v-col cols="2"></v-col>
                        <v-col>
                            <v-btn type="submit"
                                   data-add-next="true"
                                   size="large"
                                   color="green-darken-1"
                                   :loading="loading"
                                   :text="pgettext('action', 'Save and add next')"
                                   block/>
                        </v-col>
                    </v-row>
                    <v-btn v-else type="submit"
                           :loading="loading"
                           :text="pgettext('action', 'Save')"
                           size="large"
                           block/>
                </div>
            </Form>
        </v-card-text>
    </v-card>

    <PrefixModal v-if="showPrefixModal"
                 :hpoId="hpoId"
                 @close="showPrefixModal=false"/>

</template>

<script setup>
import {ErrorMessage, Field, Form} from "vee-validate";
import {computed, ref, watch} from "vue";
import {requiredMessage, stringValidator} from "@/utils/yupValidators";
import {useMutation, useQuery} from "@vue/apollo-composable";
import NewFormGroup from "../../../forms/NewFormGroup.vue";
import * as yup from "yup";
import {HpoTriageMutation} from "@/graphql/mutations/hpo/patient/triageMutation.graphql";
import {TicCardPrefixList} from "@/graphql/queries/hpo/ticCardPrefixList.graphql";
import {useLastResult} from "@/graphql/composable";

import HpoPatientModalPhcSection from "./triage/HpoPatientModalPhcSection.vue";
import SelectButton from "../SelectButton.vue";
import PrefixModal from "./triage/PrefixModal.vue";
import HpoPatientModalGenderSection from "./triage/HpoPatientModalGenderSection.vue";
import HpoPatientModalIsChildSection from "./triage/HpoPatientModalIsChildSection.vue";
import TicKeypad from "@/components/hpo/patientModal/tabs/TicKeypad.vue";
import {getLastTic, getNextTic, saveLastTic} from "@/components/hpo/patientModal/tabs/ticMemory";


yup.addMethod(yup.string, 'fourDigits', function onlyDigits(value) {
    return this.test(`test-four-digits`, gettext('Must contain four digits'), function (value) {
        return /^\d{4}$/.test(value)
    });
});

const props = defineProps({
    hpoId: {
        type: String,
        required: true
    },
    patient: {
        type: Object,
        required: true
    }
})
const emit = defineEmits(['update:patient', 'setActiveTab', 'close'])

const formRef = ref(null);
// If patient ID is missing or is null, we are creating a new patient.
const createMode = computed(() => props.patient.id === null || props.patient.id === undefined);
const patient = ref({...props.patient})
const prefix = ref(null)
const showPrefixModal = ref(false)

const schema = ref(yup.object({
    ticPrefix: stringValidator.required(requiredMessage).max(1),
    ticNumber: yup.string().nullable().required(requiredMessage).fourDigits(),
    gender: stringValidator.required(requiredMessage),
    isChild: yup.boolean().required(requiredMessage),
    priority: stringValidator.required(requiredMessage),
}))

const {result} = useQuery(TicCardPrefixList, {hpoId: props.hpoId});
const prefixOptions = useLastResult(result, [], data => data.ticCardPrefixList.list);

const {mutate, loading} = useMutation(HpoTriageMutation);

watch(patient, async (newValue, previousValue) => {
    emit('update:patient', patient.value)
})

watch(prefixOptions, async (newValue, previousValue) => {
    const options = Object.values(newValue)
    if (options.length === 1) {
        prefix.value = options[0]
    }
})

function formSubmit(values, context) {
    const submitter = context.evt?.submitter;
    // By default, add next in create mode and close in edit mode.
    let addNext = createMode.value;
    // Check if the form was submitted by clicking a button.
    if (submitter?.nodeName === "BUTTON") {
        addNext = submitter.dataset.addNext === "true";
    }

    if (addNext) {
        submitAndAddNext(values)
    } else {
        submitAndClose(values)
    }
}

function submitAndAddNext(values) {
    performSubmit(values).then(result => {
        showAlert(
            interpolate(
                pgettext('message', 'Patient %(tic)s added.'),
                {tic: `${values.ticPrefix}${values.ticNumber}`},
                true,
            )
        );
        resetForm();
    })
}

function submitAndClose(values) {
    performSubmit(values).then(result => {
        emit('close')
    })
}

function performSubmit(values) {
    const personId = patient.value.person ? patient.value.person.id : null
    return mutate({
        hpoId: props.hpoId,
        personId: personId,
        ...values
    }).then(() => {
        if (createMode.value) {
            saveLastTic(values.ticNumber);
        }
    }).catch((error) => {

    })
}

function resetForm() {
    if (formRef.value !== null) {
        formRef.value.resetForm();
    }
}

const alertMessage = ref(null);

function showAlert(message) {
    alertMessage.value = message;
    setTimeout(() => {
        alertMessage.value = null;
    }, 5000);
}

const useNextTicMessage = computed(() => {
    if (!createMode.value) {
        return null;
    }

    const lastTic = getLastTic();
    if (lastTic === null) {
        return null;
    }
    const nextTic = getNextTic();
    if (patient.value.ticNumber) {
        return null;
    }

    return interpolate(
        pgettext('action', 'Fill <strong>%(nextTic)s</strong> (last number was %(lastTic)s)'),
        {nextTic, lastTic},
        true
    );
});

function useNextTic() {
    patient.value.ticNumber = getNextTic();
}
</script>
